import _ from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {ResourceMap} from '../../../../libs/shared/core/src/store/commonAppTypes';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {renderInlineList} from '../../../../libs/shared/ui/src/helpers/listHelpers';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  ConflictingMergeCardValues,
  ContactDetailsConflictingFieldKeysEnum,
  ContactDetailsSectionWithConflictsEnum,
} from '../../../helpers/models/cardMergeHelpers';
import {CustomFieldModel} from '../../../models/api/customFieldModel';
import {useContactDetailsContext} from '../contactDetailsContext';

/*
 * Props.
 */

interface ContactMergeConflictsBannerProps {
  customFields: ResourceMap<CustomFieldModel>;
  totalMergeConflicts: number;
}

/*
 * Styles.
 */

const StyledMergeConflictsBannerContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
  background: ${(p) => p.theme.palette.yellow.shade20};
  border-radius: 16px;
  margin: 12px -8px;
  padding: 9px 10px;

  &:not(:first-child) {
    margin-top: 8px;
  }
`;

const StyledIconContainerDiv = styled.div`
  margin-right: 8px;
`;

const StyledConflictsContainerDiv = styled.div`
  flex: 1;
  font-size: ${fontSizes.verySmall};
  line-height: ${fontSizes.large};
  font-weight: ${fontWeights.medium};
`;

const StyledConflictsCountSpan = styled.span`
  font-weight: ${fontWeights.bold};
`;

/*
 * Component.
 */

export const ContactMergeConflictsBanner: FC<ContactMergeConflictsBannerProps> = (props) => {
  const theme = useUserTheme();
  const {customFields, totalMergeConflicts} = props;

  const contactDetailsContext = useContactDetailsContext();
  if (!contactDetailsContext.mergeContext.conflictingMergeValues) {
    return null;
  }

  return (
    <StyledMergeConflictsBannerContainerDiv>
      <StyledIconContainerDiv>
        <Icon name="warningFilledSmall" color={theme.palette.yellow.shade40} />
      </StyledIconContainerDiv>
      <StyledConflictsContainerDiv>
        {renderConflicts(
          totalMergeConflicts,
          contactDetailsContext.mergeContext.conflictingMergeValues,
          customFields,
        )}
      </StyledConflictsContainerDiv>
    </StyledMergeConflictsBannerContainerDiv>
  );
};

/*
 * Helpers.
 */

const renderConflicts = (
  totalMergeConflicts: number,
  conflictingMergeValues: ConflictingMergeCardValues,
  customFields: ResourceMap<CustomFieldModel>,
) => {
  const mergeConflicts = [];
  const isHeaderNameConflicting = conflictingMergeValues?.[
    ContactDetailsSectionWithConflictsEnum.HEADER
  ]?.some((val) => val.key === ContactDetailsConflictingFieldKeysEnum.NAME);
  const isHeaderAccountConflicting = conflictingMergeValues?.[
    ContactDetailsSectionWithConflictsEnum.HEADER
  ]?.some((val) => val.key === ContactDetailsConflictingFieldKeysEnum.ACCOUNT);

  if (isHeaderNameConflicting) {
    mergeConflicts.push(
      <span key="contact-details-merge-conflict-banner-header-name">
        <Trans id="contact-details-merge-conflict-banner-header-name">name</Trans>
      </span>,
    );
  }
  if (isHeaderAccountConflicting) {
    mergeConflicts.push(
      <span key="contact-details-merge-conflict-banner-header-account">
        <Trans id="contact-details-merge-conflict-banner-header-account">account</Trans>
      </span>,
    );
  }
  if (conflictingMergeValues[ContactDetailsSectionWithConflictsEnum.CONTACT_INFORMATION]) {
    mergeConflicts.push(
      ..._(conflictingMergeValues[ContactDetailsSectionWithConflictsEnum.CONTACT_INFORMATION])
        .map((conflict) => {
          const customField = customFields[conflict.id];
          if (!customField) {
            return undefined;
          }
          return `"${customField.name}"`;
        })
        .compact()
        .value(),
    );
  }

  if (mergeConflicts.length === 0) {
    return null;
  }
  return (
    <>
      <StyledConflictsCountSpan>
        <Plural
          id="contact-details-merge-conflicts-banner-total-found"
          value={totalMergeConflicts}
          one="# conflict found"
          other="# conflicts found"
        />
      </StyledConflictsCountSpan>{' '}
      <Trans id="contact-details-merge-conflict-banner-for">for</Trans> {renderInlineList(mergeConflicts)}
    </>
  );
};
